import { useEffect, useState } from 'react';
import BranchService from '../../services/branch/BranchService';
import { useAuthUser } from '../auth/useAuthUser';
import { IBranchDropdown } from '../../types/branches/branchInterfaces';

/**
 * 🔐 useBranchScope
 * ------------------------------------------------------------
 * Reemplaza el patrón repetido en las pantallas del módulo de
 * restaurante (mesas, menú, órdenes, inventario, recetas, etc.):
 * un `useState` local de `branches`/`branchId` poblado con
 * `BranchService.fetchDropdown()`, seleccionando la primera
 * sucursal por defecto.
 *
 * Ahora que cada usuario tiene una sucursal asignada
 * (`user_branches`), ese selector ya no debe pedirse manualmente
 * a roles operativos:
 * - ADMIN / SUPPORT / ACCOUNTANT (`canPickBranch = true`): siguen
 *   viendo el listado completo de sucursales y pueden elegir.
 * - Cualquier otro rol: se resuelve directo a su propia sucursal
 *   (`/api/branches/me`), sin mostrar el selector.
 */
const useBranchScope = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState('');

  useEffect(() => {
    setBranchError('');
    setLoadingBranches(true);

    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine: IBranchDropdown) => [mine]);

    request
      .then((data: IBranchDropdown[]) => {
        setBranches(data);
        if (data.length) {
          setBranchId(data[0].id);
        }
      })
      .catch((error: any) =>
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        )
      )
      .finally(() => setLoadingBranches(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickBranch]);

  return {
    branches,
    branchId,
    setBranchId,
    loadingBranches,
    branchError,
    canPickBranch,
  };
};

export default useBranchScope;
